import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/sonner";
import { Header } from "@/components/achados/Header";
import { Hero } from "@/components/achados/Hero";
import { Filters, type FilterState } from "@/components/achados/Filters";
import { ProductCard } from "@/components/achados/ProductCard";
import { ProductDetail } from "@/components/achados/ProductDetail";
import { Footer } from "@/components/achados/Footer";
import {
  DEFAULT_CATEGORIES,
  DEFAULT_STORES,
  EMPTY_PRODUCTS,
  type CatalogPayload,
  type Product,
  type RecentVideoPayload,
  deriveCategories,
  deriveMarcas,
  deriveStores,
  normalizeCatalogPayload,
  normalizeRecentVideoPayload,
} from "@/data/products";
import { useFavorites } from "@/hooks/use-favorites";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Flame, Heart, TrendingUp } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

const CATALOG_URL =
  import.meta.env.VITE_CATALOG_URL ||
  "https://storage.googleapis.com/automacaoposts-495322-instagram-videos/catalog/latest.json";
const PAGE_SIZE = 24;

const RECENT_VIDEOS_URL =
  import.meta.env.VITE_RECENT_VIDEOS_URL ||
  "https://storage.googleapis.com/automacaoposts-495322-instagram-videos/catalog/recent-videos.json";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Achados do Dia" },
      {
        name: "description",
        content:
          "Encontre os melhores achadinhos, promocoes e ofertas do dia em moda, beleza, acessorios, casa e muito mais.",
      },
      { property: "og:title", content: "Achados do Dia" },
      { property: "og:description", content: "Curadoria diaria de achadinhos das suas lojas favoritas." },
    ],
  }),
  component: CatalogPage,
});

const CATEGORY_ICONS: Record<string, string> = {
  "Moda Feminina": "👗",
  Blusas: "👚",
  Calcas: "👖",
  Shorts: "🩳",
  Vestidos: "💃",
  Conjuntos: "🛍️",
  Roupas: "✨",
  Promocoes: "🔥",
  "Mais Vendidos": "⭐",
};

async function fetchCatalog(): Promise<CatalogPayload> {
  const response = await fetch(CATALOG_URL, {
    headers: {
      Accept: "application/json",
      "Cache-Control": "no-cache",
    },
  });
  if (!response.ok) {
    throw new Error(`Falha ao carregar catalogo: ${response.status}`);
  }
  return response.json();
}

async function fetchRecentVideos(): Promise<RecentVideoPayload> {
  const response = await fetch(RECENT_VIDEOS_URL, {
    headers: {
      Accept: "application/json",
      "Cache-Control": "no-cache",
    },
  });
  if (!response.ok) {
    throw new Error(`Falha ao carregar videos recentes: ${response.status}`);
  }
  return response.json();
}

export function CatalogPage() {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("best");
  const [showOnlyFavs, setShowOnlyFavs] = useState(false);
  const [detail, setDetail] = useState<Product | null>(null);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  // Renderizar o catálogo inteiro de uma vez colocava ~360 cartões no DOM já na
  // primeira pintura. Mostramos por lotes e carregamos o próximo quando o fim da
  // grade entra em cena — para quem rola a página o comportamento é o mesmo.
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const [filters, setFilters] = useState<FilterState>({
    categorias: [],
    lojas: [],
    marcas: [],
    desconto: 0,
    avaliacao: 0,
    precoMax: 300,
  });

  const { isFav, toggle, ids: favIds } = useFavorites();
  const catalogQuery = useQuery({
    queryKey: ["daily-catalog"],
    queryFn: fetchCatalog,
    staleTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
  });
  const recentVideosQuery = useQuery({
    queryKey: ["recent-published-videos"],
    queryFn: fetchRecentVideos,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });

  const products = useMemo(
    () => normalizeCatalogPayload(catalogQuery.data),
    [catalogQuery.data],
  );
  const recentVideos = useMemo(
    () => normalizeRecentVideoPayload(recentVideosQuery.data),
    [recentVideosQuery.data],
  );

  const categories = useMemo(() => deriveCategories(products), [products]);
  const stores = useMemo(() => deriveStores(products), [products]);
  const marcas = useMemo(() => deriveMarcas(products), [products]);

  const filteredProducts = useMemo(() => {
    let list = products.filter((product) => {
      if (query && !product.nome.toLowerCase().includes(query.toLowerCase())) return false;
      if (filters.categorias.length && !filters.categorias.includes(product.categoria)) return false;
      if (filters.lojas.length && !filters.lojas.includes(product.loja)) return false;
      if (filters.marcas.length && !filters.marcas.includes(product.marca)) return false;
      if (product.desconto < filters.desconto) return false;
      if (product.avaliacao < filters.avaliacao) return false;
      if (product.precoAtual > filters.precoMax) return false;
      if (showOnlyFavs && !favIds.includes(product.id)) return false;
      return true;
    });
    switch (sort) {
      case "price-asc":
        list = [...list].sort((a, b) => a.precoAtual - b.precoAtual);
        break;
      case "price-desc":
        list = [...list].sort((a, b) => b.precoAtual - a.precoAtual);
        break;
      case "discount":
        list = [...list].sort((a, b) => b.desconto - a.desconto);
        break;
      case "rating":
        list = [...list].sort((a, b) => b.avaliacao - a.avaliacao);
        break;
      case "best":
        list = [...list].sort(
          (a, b) =>
            (b.vendidos || 0) - (a.vendidos || 0) ||
            Number(!!b.maisVendido) - Number(!!a.maisVendido),
        );
        break;
      default:
        list = [...list].sort(
          (a, b) =>
            (b.vendidos || 0) - (a.vendidos || 0) ||
            Number(!!b.maisVendido) - Number(!!a.maisVendido) ||
            b.avaliacao - a.avaliacao,
        );
        break;
    }
    return list;
  }, [products, query, filters, sort, showOnlyFavs, favIds]);

  // Filtro/busca novos = lista nova: volta ao primeiro lote.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [query, sort, showOnlyFavs, filters]);

  const visibleProducts = useMemo(
    () => filteredProducts.slice(0, visibleCount),
    [filteredProducts, visibleCount],
  );

  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel || visibleCount >= filteredProducts.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisibleCount((current) => current + PAGE_SIZE);
        }
      },
      { rootMargin: "600px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [visibleCount, filteredProducts.length]);

  const featured = useMemo(() => {
    const highlighted = products.filter((product) => product.destaque);
    return (highlighted.length ? highlighted : products).slice(0, 4);
  }, [products]);

  const mostClicked = useMemo(() => {
    const best = products.filter((product) => product.maisVendido);
    return (
      best.length
        ? [...best].sort((a, b) => (b.vendidos || 0) - (a.vendidos || 0))
        : [...products].sort((a, b) => (b.vendidos || 0) - (a.vendidos || 0) || b.avaliacao - a.avaliacao)
    ).slice(0, 4);
  }, [products]);

  const heroStats = useMemo(() => {
    const count = products.length || catalogQuery.data?.count || 0;
    const maxDiscount = products.length
      ? Math.max(...products.map((product) => product.desconto))
      : 70;
    return {
      offersLabel: count > 0 ? `+${count}` : "+0",
      storesLabel: `${stores.length || DEFAULT_STORES.length}`,
      discountLabel: `-${maxDiscount || 0}%`,
    };
  }, [catalogQuery.data?.count, products, stores.length]);

  const scrollToGrid = () =>
    gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const setCatFromHeader = (category: string) => {
    setFilters((current) => ({ ...current, categorias: category ? [category] : [] }));
    scrollToGrid();
  };

  const categoryOptions = categories.length ? categories : [...DEFAULT_CATEGORIES];

  return (
    <div className="min-h-screen bg-background">
      <Header
        query={query}
        onQuery={setQuery}
        favCount={favIds.length}
        onCategory={setCatFromHeader}
        categories={categoryOptions}
        onOpenFilters={() => setFilterSheetOpen(true)}
      />
      <main>
        <Hero onSeeOffers={scrollToGrid} stats={heroStats} recentVideos={recentVideos} />

        <section className="mx-auto max-w-7xl px-4 pt-8 md:px-6">
          <div className="mb-4 flex items-end justify-between">
            <h2 className="font-display text-2xl md:text-3xl">Navegue por categoria</h2>
          </div>
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-5 md:grid-cols-10">
            {[...categoryOptions, "Promocoes", "Mais Vendidos"].map((category) => (
              <button
                key={category}
                onClick={() =>
                  setCatFromHeader(["Promocoes", "Mais Vendidos"].includes(category) ? "" : category)
                }
                className="group flex flex-col items-center gap-2 rounded-2xl border border-border/70 bg-card p-3 text-center transition hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-[var(--shadow-card)]"
              >
                <span className="grid h-12 w-12 place-items-center rounded-full bg-secondary text-2xl transition group-hover:bg-[image:var(--gradient-primary)]">
                  {CATEGORY_ICONS[category] || "✨"}
                </span>
                <span className="text-[11px] font-medium leading-tight text-foreground/80">{category}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 pt-14 md:px-6">
          <div className="mb-5 flex items-end justify-between">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-2.5 py-1 text-[11px] font-medium text-primary-strong">
                <Flame className="h-3 w-3" /> Hoje
              </span>
              <h2 className="mt-2 font-display text-3xl md:text-4xl">Ofertas em destaque</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {featured.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                isFav={isFav(product.id)}
                onToggleFav={toggle}
                onOpenDetail={setDetail}
              />
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 pt-14 md:px-6">
          <div className="mb-5 flex items-end justify-between">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-2.5 py-1 text-[11px] font-medium text-primary-strong">
                <TrendingUp className="h-3 w-3" /> Tendencia
              </span>
              <h2 className="mt-2 font-display text-3xl md:text-4xl">Achadinhos mais clicados</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {mostClicked.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                isFav={isFav(product.id)}
                onToggleFav={toggle}
                onOpenDetail={setDetail}
              />
            ))}
          </div>
        </section>

        <section ref={gridRef} className="mx-auto max-w-7xl scroll-mt-32 px-4 pt-16 md:px-6">
          <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="font-display text-3xl md:text-4xl">Todos os achadinhos</h2>
              <p className="text-sm text-muted-foreground">
                {filteredProducts.length} produtos encontrados
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant={showOnlyFavs ? "default" : "outline"}
                onClick={() => setShowOnlyFavs((value) => !value)}
                className={`rounded-full ${showOnlyFavs ? "bg-primary" : ""}`}
              >
                <Heart className={`mr-1.5 h-4 w-4 ${showOnlyFavs ? "fill-current" : ""}`} />
                Meus favoritos
              </Button>

              <Sheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen}>
                <SheetContent side="left" className="w-[88vw] max-w-sm overflow-y-auto p-0">
                  <SheetHeader className="border-b p-4">
                    <SheetTitle>Filtros</SheetTitle>
                  </SheetHeader>
                  <div className="p-4">
                    <MobileFilters
                      state={filters}
                      set={setFilters}
                      categories={categoryOptions}
                      stores={stores.length ? stores : [...DEFAULT_STORES]}
                      marcas={marcas}
                    />
                  </div>
                </SheetContent>
              </Sheet>

              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="w-[180px] rounded-full" aria-label="Ordenar ofertas">
                  <SelectValue placeholder="Ordenar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="relevance">Mais relevantes</SelectItem>
                  <SelectItem value="price-asc">Menor preco</SelectItem>
                  <SelectItem value="price-desc">Maior preco</SelectItem>
                  <SelectItem value="discount">Maior desconto</SelectItem>
                  <SelectItem value="best">Mais vendidos</SelectItem>
                  <SelectItem value="rating">Melhor avaliados</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-6">
            <Filters
              state={filters}
              set={setFilters}
              categories={categoryOptions}
              stores={stores.length ? stores : [...DEFAULT_STORES]}
              marcas={marcas}
            />
            <div className="min-w-0 flex-1">
              {catalogQuery.isLoading ? (
                <div className="rounded-3xl border border-dashed border-border bg-card/50 p-12 text-center">
                  <p className="font-display text-xl">Carregando achadinhos do dia...</p>
                </div>
              ) : catalogQuery.isError ? (
                <div className="rounded-3xl border border-dashed border-border bg-card/50 p-12 text-center">
                  <p className="font-display text-xl">Nao foi possivel carregar o catalogo</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Tente atualizar a pagina em alguns instantes.
                  </p>
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="rounded-3xl border border-dashed border-border bg-card/50 p-12 text-center">
                  <p className="font-display text-xl">Nenhum achadinho encontrado</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Tente ajustar a busca ou os filtros para ver mais produtos.
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
                    {visibleProducts.map((product) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        isFav={isFav(product.id)}
                        onToggleFav={toggle}
                        onOpenDetail={setDetail}
                      />
                    ))}
                  </div>
                  {visibleCount < filteredProducts.length ? (
                    <div ref={loadMoreRef} className="mt-8 flex justify-center">
                      <button
                        type="button"
                        onClick={() => setVisibleCount((current) => current + PAGE_SIZE)}
                        className="rounded-full border border-border bg-card px-6 py-2.5 text-sm font-medium text-foreground/80 transition hover:border-primary/40 hover:text-foreground"
                      >
                        Ver mais achadinhos
                      </button>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <ProductDetail product={detail} onClose={() => setDetail(null)} />
      <Toaster position="top-center" richColors />
    </div>
  );
}

function MobileFilters(props: {
  state: FilterState;
  set: (state: FilterState) => void;
  categories: string[];
  stores: string[];
  marcas: string[];
}) {
  return (
    <div className="[&>aside]:!static [&>aside]:!block [&>aside]:!w-full">
      <Filters {...props} />
    </div>
  );
}
